/**
 * Movement in the health score since the previous audit.
 *
 * A score with no baseline says nothing about whether the site is getting
 * better, which is the only question a returning user has. Nothing is claimed
 * on a first audit: there is no previous score, and inventing one would fake a
 * trend out of a single data point.
 */

import { ArrowRightIcon } from '@/components/icons';
import { cn } from '@/utils/format';

export function HealthTrend({
  score,
  previous,
  hasAudit,
}: {
  score: number | null;
  previous: number | null;
  hasAudit: boolean;
}) {
  if (score === null || !hasAudit) return null;

  if (previous === null) {
    return <p className="mt-2.5 text-[11px] text-ink-subtle">first audit — no comparison yet</p>;
  }

  const delta = score - previous;
  if (delta === 0) {
    return <p className="mt-2.5 text-[11px] text-ink-subtle">unchanged since last audit</p>;
  }

  const up = delta > 0;
  return (
    <p
      className={cn(
        'tnum mt-2.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        up ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger',
      )}
    >
      <ArrowRightIcon className={cn('h-3 w-3', up ? '-rotate-45' : 'rotate-45')} />
      {up ? '+' : ''}
      {delta} since last audit
    </p>
  );
}
